import React, { useEffect, useRef, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { formatBC } from "@/lib/format";
import { toast } from "sonner";
import { MessageCircle, Send, CheckCircle2, RefreshCw, User, ShieldCheck, Inbox, Clock } from "lucide-react";

function MessageRow({ msg }) {
  const isUser = msg.role === "user";
  const isAdmin = msg.role === "admin";
  const align = isUser ? "items-start" : "items-end";
  const colorClass = isUser
    ? "bg-[#1E3A8A] text-white"
    : isAdmin
    ? "bg-[#DC2626] text-white"
    : "bg-[#FBBF24]/10 border border-[#FBBF24]/30 text-white";
  return (
    <div className={`flex flex-col ${align}`} data-testid={`admin-msg-${msg.id}`}>
      <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1 font-bold">
        {isUser ? msg.sender_name || "Member" : isAdmin ? "You · Admin" : "Zen AI"}
        <span className="ml-2 text-white/30 normal-case tracking-normal">{new Date(msg.created_at).toLocaleString()}</span>
      </div>
      <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap leading-relaxed ${colorClass}`}>
        {msg.content}
      </div>
    </div>
  );
}

export default function AdminChat() {
  const [sessions, setSessions] = useState([]);
  const [filter, setFilter] = useState("escalated");
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);
  const scrollRef = useRef(null);

  const loadSessions = async () => {
    const { data } = await api.get(`/admin/chat?status=${filter}`);
    setSessions(data);
    const { data: s } = await api.get("/admin/chat/stats");
    setStats(s);
  };
  const loadDetail = async (id) => {
    const { data } = await api.get(`/chat/sessions/${id}`);
    setDetail(data);
  };
  useEffect(() => { loadSessions(); }, [filter]);
  useEffect(() => { if (activeId) loadDetail(activeId); }, [activeId]);
  useEffect(() => {
    const i = setInterval(() => {
      loadSessions();
      if (activeId) loadDetail(activeId);
    }, 15000);
    return () => clearInterval(i);
  }, [activeId, filter]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [detail]);

  const send = async (e) => {
    e?.preventDefault?.();
    if (!reply.trim() || !activeId) return;
    setBusy(true);
    try {
      await api.post(`/admin/chat/${activeId}/reply`, { content: reply.trim() });
      setReply("");
      await Promise.all([loadDetail(activeId), loadSessions()]);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const resolve = async () => {
    if (!activeId) return;
    if (!window.confirm("Mark this conversation as resolved?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/chat/${activeId}/resolve`);
      toast.success("Resolved");
      setActiveId(null); setDetail(null);
      await loadSessions();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="admin-chat-panel">
      {/* Sidebar — session list */}
      <div className="lg:col-span-1 glass-card p-3 flex flex-col h-[70vh]">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[#FBBF24] font-bold">
            <Inbox size={14} /> Inbox
            {stats?.unread > 0 && <span className="bg-[#DC2626] text-white text-[10px] px-1.5 py-0.5 rounded-full font-mono-num" data-testid="inbox-unread-badge">{stats.unread}</span>}
          </div>
          <button onClick={loadSessions} className="text-white/55 hover:text-white text-xs" data-testid="inbox-refresh"><RefreshCw size={14} /></button>
        </div>
        <div className="flex gap-1 mb-3">
          {["escalated", "resolved"].map((s) => (
            <button
              key={s}
              onClick={() => { setFilter(s); setActiveId(null); }}
              className={`flex-1 px-2 py-1 rounded text-[10px] uppercase tracking-widest font-bold ${filter === s ? "bg-[#1E3A8A] text-white" : "bg-white/5 text-white/55 hover:bg-white/10"}`}
              data-testid={`inbox-filter-${s}`}
            >
              {s} ({stats?.[s === "escalated" ? "escalated" : "resolved"] ?? "—"})
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto space-y-2 pr-1">
          {sessions.length === 0 && <div className="text-white/40 text-xs text-center py-8" data-testid="inbox-empty">No conversations.</div>}
          {sessions.map((s) => {
            const active = s.id === activeId;
            return (
              <button
                key={s.id}
                onClick={() => setActiveId(s.id)}
                className={`w-full text-left p-3 rounded-lg border transition-all ${active ? "bg-[#1E3A8A] border-[#FBBF24]" : "bg-white/[0.03] border-white/5 hover:bg-white/[0.06]"}`}
                data-testid={`inbox-session-${s.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold truncate flex items-center gap-1">
                    <User size={12} className="text-white/55" /> {s.user_name}
                  </div>
                  {s.unread_for_admin && <span className="w-2 h-2 rounded-full bg-[#DC2626]" />}
                </div>
                <div className="text-[11px] text-white/55 mt-0.5 truncate">{s.last_preview}</div>
                <div className="text-[10px] text-white/35 mt-1 flex items-center gap-1">
                  <Clock size={10} /> {new Date(s.last_message_at).toLocaleString()}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Right pane — conversation */}
      <div className="lg:col-span-2 glass-card flex flex-col h-[70vh] overflow-hidden">
        {!detail ? (
          <div className="flex-1 flex flex-col items-center justify-center text-white/45 gap-2" data-testid="admin-chat-empty">
            <MessageCircle className="text-white/30" size={36} />
            <div className="text-sm">Select a conversation from the inbox.</div>
          </div>
        ) : (
          <>
            <div className="px-5 py-4 border-b border-white/5 flex flex-wrap items-center gap-3 bg-[#0F2042]/40">
              <div className="flex-1 min-w-0">
                <div className="font-display text-lg font-bold flex items-center gap-2">
                  {detail.session.user_name}
                  <span className="text-[10px] uppercase tracking-widest text-[#FBBF24] font-bold px-2 py-0.5 rounded-full bg-[#FBBF24]/15 border border-[#FBBF24]/40 flex items-center gap-1">
                    <ShieldCheck size={10} /> {detail.session.status}
                  </span>
                </div>
                <div className="text-xs text-white/55">{detail.session.user_email}</div>
              </div>
              {detail.session.status !== "resolved" && (
                <button onClick={resolve} disabled={busy} className="text-xs px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 font-bold disabled:opacity-50 flex items-center gap-1" data-testid="resolve-session-btn">
                  <CheckCircle2 size={14} /> Mark resolved
                </button>
              )}
            </div>
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="admin-chat-thread">
              {detail.messages.map((m) => <MessageRow key={m.id} msg={m} />)}
            </div>
            {detail.session.status !== "resolved" && (
              <form onSubmit={send} className="border-t border-white/10 p-3 bg-[#0F2042]/60 flex gap-2">
                <textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(e); }}
                  rows={2}
                  placeholder="Type your reply… (Ctrl/Cmd+Enter to send)"
                  className="flex-1 zfd-input"
                  data-testid="admin-reply-input"
                  disabled={busy}
                />
                <button type="submit" disabled={busy || !reply.trim()} className="bg-[#FBBF24] text-[#0F2042] hover:bg-yellow-300 px-4 rounded-md font-bold disabled:opacity-40 flex items-center" data-testid="admin-reply-send">
                  <Send size={16} />
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}
